// =============================================================================
// projetShared.js — Référentiels du domaine « projet » extraits d'App.jsx
// (Révision S1-1). Déplacement pur : chaque bloc est repris à l'identique
// depuis App.jsx, seul le préfixe `export` a été ajouté.
// Contenu : STATUTS_MATURITE (axe sémantique 2026), palettes de domaines
// (DOMAINE_COLORS, DOMAINE_NAME_COLORS) + getDomaineColor, risques
// assurantiels (RISQUES_ASSURANCE) et items typés étapes/livrables
// (ITEM_STATUT_META, normalizeItem).
// Consommé par App.jsx (ProjetCard, ProjetFicheStructuree, SimplifieView…)
// et par ProjetModal.js.
// =============================================================================

// ─── Statut de maturité (axe sémantique principal — refonte 2026) ───────────
// Un seul axe ordonné, du plus actionnable au plus pérenne, qui remplace
// progressivement les 9 statuts historiques. Cf. plan §2.1.
//
//   🟢 activable          : opportunité OUVERTE, adoption mutuelle par mutuelle
//   🟠 construction       : chantier actif (cadrage / instruction / déploiement)
//   🔵 etude              : exploration, idée, étude tarifaire
//   🟣 acquis             : patrimoine commun finalisé, valorisation
//   🔄 mission_permanente : activité continue avec revue périodique
// Refonte 2026-05-20 : "Acquis" supprimé (ne servait à rien d'après PM).
// Mission permanente récupère sa couleur violette (#7C4DBB) pour cohérence
// avec la palette d'origine du statut supprimé.
export const STATUTS_MATURITE = [
  { v: "activable",          l: "Activable",          color: "#94C11F",
    icon: "🟢", desc: "Catalogue d'opportunités ouvertes (adoption mutuelle par mutuelle)" },
  { v: "construction",       l: "En construction",    color: "#F49800",
    icon: "🟠", desc: "Chantier actif (cadrage / instruction / déploiement)" },
  { v: "etude",              l: "À l'étude",          color: "#2AA9E1",
    icon: "🔵", desc: "Exploration, étude, négociation amont" },
  { v: "mission_permanente", l: "Mission permanente", color: "#951B81",
    icon: "🟣", desc: "Activité continue, revue périodique" },
];

export const DOMAINE_COLORS = {
  "0":"#9CA3AF","1":"#378ADD","2":"#F49800","3":"#1D948A","4":"#951B81","5":"#5CB85C",
};

// Mapping nom de domaine → couleur officielle Enio (cohérence visuelle)
export const DOMAINE_NAME_COLORS = {
  "Développement & Fidélisation": "#1D948A",        // teal
  "Développement et Fidélisation": "#1D948A",
  "Affaires extérieures": "#378ADD",                 // bleu
  "Affaires Extérieures": "#378ADD",
  "Impact": "#5CB85C",                               // vert
  "Pilotage éco. & réglementaire": "#951B81",       // violet
  "Pilotage Économique et Réglementaire": "#951B81",
  "Ressources": "#F49800",                           // orange
};

// Refonte 2026-05-20 — Risques assurantiels pour les offres et produits.
// Quand un projet est marqué `offreProduit=true`, l'admin peut sélectionner
// les risques métiers couverts. Le champ "autre" autorise un texte libre
// stocké dans `risquesConcernesAutre`.
export const RISQUES_ASSURANCE = [
  { v: "sante",      l: "Santé",        icon: "🏥", color: "#94C11F" },
  { v: "prevoyance", l: "Prévoyance",   icon: "🛡",  color: "#2AA9E1" },
  { v: "iard",       l: "IARD",         icon: "🏠", color: "#F49800" },
  { v: "retraite",   l: "Retraite",     icon: "👴", color: "#951B81" },
  { v: "emprunteur", l: "Emprunteur",   icon: "🏦", color: "#E20816" },
  { v: "autre",      l: "Autre",        icon: "📝", color: "#6B7280" },
];

// Retourne la couleur d'un projet selon l'ordre de priorité :
//   1. p.couleur (choix explicite de l'admin)
//   2. Nom du domaine (mapping officiel Enio)
//   3. Préfixe de référence (legacy)
//   4. Gris fallback
export function getDomaineColor(refOrProjet) {
  // Compat : si on passe un string, c'est une ref legacy
  if (typeof refOrProjet === "string") {
    return DOMAINE_COLORS[String(refOrProjet||"").charAt(0)] || "#9CA3AF";
  }
  const p = refOrProjet || {};
  if (p.couleur && typeof p.couleur === "string") return p.couleur;
  const domNames = Array.isArray(p.domaines) && p.domaines.length > 0 ? p.domaines : (p.domaine ? [p.domaine] : []);
  for (const d of domNames) {
    if (DOMAINE_NAME_COLORS[d]) return DOMAINE_NAME_COLORS[d];
  }
  return DOMAINE_COLORS[String(p.ref||"").charAt(0)] || "#9CA3AF";
}

// ── Statuts communs aux items typés (étapes ET livrables) ──────────────────
// "fait" ✅ / "en_cours" ⏱️ / "a_venir" ➝ — calque les conventions utilisées
// dans nos slides projet (cf. Enio - MutRATP.pptx).
export const ITEM_STATUT_META = {
  a_venir:  { icon: "➝",  label: "À venir",  color: "#9CA3AF" },
  en_cours: { icon: "⏱️", label: "En cours", color: "#F49800" },
  fait:     { icon: "✅", label: "Réalisé",  color: "#94C11F" },
};

// Normalise un item : peut être une string legacy → {texte, statut, date, niveau}
// Préserve les champs déjà typés. Statut par défaut configurable
// (ex : livrables legacy = "fait", étapes legacy = "a_venir").
export const normalizeItem = (raw, defaultStatut = "a_venir") => {
  if (raw == null) return null;
  if (typeof raw === "string") return { texte: raw, statut: defaultStatut, date: "", niveau: 0 };
  return {
    texte: raw.texte || "",
    statut: raw.statut || defaultStatut,
    date: raw.date || "",
    niveau: raw.niveau === 1 ? 1 : 0,
  };
};
